import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuX } from "react-icons/lu";
import { colors, components, effects, typography } from "../../../design/tokens";

interface SideMenuProjectDeleteConfirmationProps {
  projectName: string;
  onClose: () => void;
  onDelete: () => void;
}

export default function SideMenuProjectDeleteConfirmation({
  projectName,
  onClose,
  onDelete,
}: SideMenuProjectDeleteConfirmationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsVisible(true);
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    closeTimeout.current = setTimeout(() => {
      onClose();
    }, 200);
  };

  return createPortal(
    <div
      className={`${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        } fixed inset-0 z-50 p-4
      flex items-center justify-center
      ${effects.transition.opacity}
      ${components.modal.overlay}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className={`relative w-full max-w-[420px]
          ${effects.borderRadius.lg}
          border ${colors.border.gray[800]}
          overflow-hidden`}
        style={{
          backgroundColor: colors.hex.surfaceSecondary,
          boxShadow: effects.shadow.custom.modal,
        }}
      >
        {/* Close button */}
        <button
          className={`absolute top-4 right-4 z-10
            ${components.modal.closeButton}`}
          onClick={handleClose}
          aria-label="Close"
        >
          <LuX size={20} />
        </button>

        {/* Content */}
        <div className="flex flex-col items-center gap-8 px-10 py-12 text-center">
          {/* Title */}
          <h2
            className={`${typography.size.xl} ${typography.weight.semibold} ${colors.text.primary}`}
          >
            Delete Project?
          </h2>

          {/* Description */}
          <div className={`${typography.size.base} ${colors.text.secondary} space-y-1`}>
            <p>
              You are about to delete{" "}
              <span className={`${colors.text.primary} ${typography.weight.semibold}`}>
                {projectName}
              </span>
              .
            </p>
            <p>Are you sure you want to delete this project?</p>
          </div>

          {/* Actions */}
          <div className={components.modal.actions}>
            <button
              className={components.form.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className={`px-5 py-2 bg-red-600 text-white
                ${effects.borderRadius.full}
                ${typography.size.sm} ${typography.weight.semibold}
                ${colors.hover.opacity} ${effects.transition.default}`}
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
